import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, CardBody, Col, Input, Row, Spinner } from 'reactstrap';
import { FaArrowLeft, FaPrint, FaSearch, FaUser } from 'react-icons/fa';
import clientService from '../../services/clientService';
import { formatPrice } from '../../helpers/utility';
import Pagination from '../Common/Pagination';

const name = client => client.entreprise || [client.nom, client.prenom].filter(Boolean).join(' ') || client.code;
const balanceLabel = value => Number(value) > 0 ? 'Reste à payer' : Number(value) < 0 ? 'Avoir du client' : 'Tout est payé';
const money = value => formatPrice(Math.abs(Number(value || 0)));

const MouvementSoldeClient = () => {
    const [params, setParams] = useSearchParams();
    const clientId = params.get('clientId');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => { setQuery(search); setPage(0); }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        const request = clientId
            ? clientService.situation(clientId, { page, size: 25 }, controller.signal)
            : clientService.situations({ query, page, size: 25 }, controller.signal);
        request.then(({ data: response }) => {
            if (controller.signal.aborted) return;
            const result = response.body;
            if (page > 0 && page >= result.totalPages) { setPage(Math.max(0, result.totalPages - 1)); return; }
            setData(result);
            setLoading(false);
        }).catch(() => {
            if (!controller.signal.aborted) { setError(true); setLoading(false); }
        });
        return () => controller.abort();
    }, [clientId, query, page, refresh]);

    const selectClient = id => { setPage(0); setData(null); setParams(id ? { clientId: id } : {}); };
    const summary = data?.summary;
    const rows = data?.content || [];

    return <div>
        <Row className="mb-3"><Col><Card><CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h4><FaUser className="me-2" />Situation client</h4>
                <div className="d-print-none d-flex gap-2">
                    {clientId && <Button outline onClick={() => selectClient(null)}><FaArrowLeft /> Tous les clients</Button>}
                    <Button outline onClick={() => setRefresh(value => value + 1)}>Actualiser</Button>
                    <Button color="primary" onClick={() => window.print()} disabled={loading || error}><FaPrint /> Imprimer cette page</Button>
                </div>
            </div>
            <p className="mb-0 text-muted">Consultez ce que le client a acheté, payé et retourné, depuis le début.</p>
        </CardBody></Card></Col></Row>
        {!clientId && <div className="input-group mb-3 d-print-none">
            <span className="input-group-text"><FaSearch /></span>
            <Input aria-label="Rechercher un client" placeholder="Nom ou code du client" value={search} onChange={event => setSearch(event.target.value)} />
        </div>}
        {loading ? <div className="text-center p-5"><Spinner /><p>Chargement…</p></div>
            : error ? <div role="alert" className="alert alert-danger">Impossible de charger la situation. <Button onClick={() => setRefresh(value => value + 1)}>Réessayer</Button></div>
            : <>
                {summary && <>
                    <h5>{name(summary.client)} <small className="text-muted">{summary.client.code}</small></h5>
                    <Row>
                        {[
                            ['Total ventes', summary.totalVentes, 'Produits achetés'],
                            ['Total paiements', summary.totalPaiements, 'Paiements enregistrés, avances comprises'],
                            ['Total retours', summary.totalRetours, 'Produits rendus'],
                            ['Remboursé au client', summary.totalRemboursements, 'Argent rendu au client'],
                        ].map(([label, amount, hint]) => <Col sm="6" lg="3" key={label} className="mb-3"><Card><CardBody>
                            <div>{label}</div><h4>{money(amount)}</h4><small className="text-muted">{hint}</small>
                        </CardBody></Card></Col>)}
                    </Row>
                    <div className={`alert ${Number(summary.solde) > 0 ? 'alert-warning' : 'alert-success'}`}>
                        <strong className="fs-4">{balanceLabel(summary.solde)} : {money(summary.solde)}</strong>
                        <div>{Number(summary.solde) > 0 ? 'Le client doit encore payer ce montant.' : Number(summary.solde) < 0 ? 'Ce montant reste en faveur du client.' : 'Le client ne doit plus rien.'}</div>
                    </div>
                    <h5>Historique des achats, paiements et retours</h5>
                </>}
                <Card><CardBody>
                    <div className="table-responsive"><table className="table table-hover align-middle">
                        <thead><tr>{(clientId ? ['Date', 'Opération', 'Référence', 'Montant'] : ['Client', 'Total ventes', 'Total paiements', 'Retours', 'Remboursé', 'Solde client', '']).map((label, index) => <th key={index}>{label}</th>)}</tr></thead>
                        <tbody>{clientId ? rows.map((row, index) => <tr key={`${row.type}-${row.reference}-${index}`}>
                            <td>{row.date ? row.date.split('-').reverse().join('/') : '—'}</td>
                            <td><span className={`badge ${row.type === 'Vente' ? 'bg-primary' : row.type === 'Remboursement' ? 'bg-warning text-dark' : 'bg-success'}`}>{row.type}</span></td>
                            <td>{row.reference}</td><td>{money(row.montant)}</td>
                        </tr>) : rows.map(row => <tr key={row.client.id}>
                            <td><strong>{name(row.client)}</strong><div className="text-muted">{row.client.code} · {row.client.telephone1}</div></td>
                            <td>{money(row.totalVentes)}</td><td>{money(row.totalPaiements)}</td>
                            <td>{money(row.totalRetours)}</td><td>{money(row.totalRemboursements)}</td>
                            <td className={Number(row.solde) > 0 ? 'text-danger' : 'text-success'}><strong>{money(row.solde)}</strong><div>{balanceLabel(row.solde)}</div></td>
                            <td><Button color="primary" size="sm" onClick={() => selectClient(row.client.id)}>Voir le détail</Button></td>
                        </tr>)}
                            {!rows.length && <tr><td colSpan={clientId ? 4 : 7} className="text-center py-4">{clientId ? 'Aucun achat, paiement ou retour pour ce client.' : 'Aucun client trouvé.'}</td></tr>}
                        </tbody>
                    </table></div>
                    <div className="d-print-none"><Pagination currentPage={page} totalPages={data?.totalPages || 0} onPageChange={setPage} /></div>
                    <small className="text-muted">{data?.totalElements || 0} {clientId ? 'opération(s)' : 'client(s)'} · Page {page + 1} sur {Math.max(1, data?.totalPages || 0)}</small>
                </CardBody></Card>
                <p className="text-muted mt-3">Solde = ventes − retours − paiements + remboursements.</p>
                <p className="text-muted">Les chèques enregistrés sont inclus, même en attente d’encaissement. Les cautions et opérations annulées sont exclues.</p>
            </>}
    </div>;
};

export default MouvementSoldeClient;
